import Button from '@jetbrains/ring-ui/components/button/button'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import Tooltip from '@jetbrains/ring-ui/components/tooltip/tooltip'
import classNames from 'classnames'
import React, {memo, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {useAppDispatch} from '../../../../../../hooks/react-redux'
import {useBooleanQueryParam} from '../../../../../../hooks/routes'
import {getHrefWithQueryParams} from '../../../../../../routes'
import {
  feedbackForm,
  ShowFeedbackFormReasons,
} from '../../../components/common/Feedback/Feedback.slice'
import RunButton from '../../../components/common/RunButton/RunButton'
import type {pipelineHeadFragment$key} from '../../../hooks/__generated__/pipelineHeadFragment.graphql'
import {usePipelineHeadId, usePipelineId} from '../../../hooks/pipeline'
import {useEditPipeline, useEditPipelineYAML} from '../../EditPipelinePage.hooks'
import {pipelineDraft, pipelineError, pipelineYaml} from '../../slices/EditPipelinePage.slices'

import useSaveChangesError from './hooks/useSaveChangesError'

import styles from './SaveChanges.css'

type Props = {
  className?: string
  pipelineHeadKey: pipelineHeadFragment$key | null
}

function SaveChanges({className, pipelineHeadKey}: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const pipelineId = usePipelineId()
  const isYaml = useBooleanQueryParam('yaml')

  const saveChangesError = useSaveChangesError(pipelineId)
  const editPipeline = useEditPipeline()
  const editPipelineYAML = useEditPipelineYAML()

  const hasErrors = !editPipelineYAML.isValid || saveChangesError.hasErrors
  const hasChanges = editPipelineYAML.hasChanges || editPipeline.hasChanges
  const isLoading = editPipelineYAML.isLoading || editPipeline.isLoading

  const errorJobsLabel = isYaml ? '' : saveChangesError.errorJobsLabel
  const handlerSaveButtonClick = isYaml
    ? editPipelineYAML.saveChangesHandler
    : editPipeline.handlerSaveButtonClick

  const reset = () => {
    dispatch(pipelineDraft.actions.reset(pipelineId))
    dispatch(pipelineYaml.actions.reset(pipelineId))
    dispatch(pipelineError.actions.resetError())
    navigate(getHrefWithQueryParams(location, null, {yaml: 'false'}), {replace: true})
  }

  const headId = usePipelineHeadId(pipelineHeadKey)
  const popupProps = useMemo(
    () => ({
      offset: 10,
      autoCorrectTopOverflow: true,
      directions: [Directions.BOTTOM_CENTER],
    }),
    [],
  )

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.actions}>
        {hasChanges && (
          <React.Fragment>
            <Button onClick={reset} className={styles.button} disabled={isLoading}>
              {'Cancel'}
            </Button>
            <Tooltip popupProps={popupProps} title={errorJobsLabel}>
              <Button
                disabled={hasErrors}
                onClick={handlerSaveButtonClick}
                className={styles.button}
                loader={isLoading}
              >
                {'Save'}
              </Button>
            </Tooltip>
          </React.Fragment>
        )}
        <Tooltip popupProps={popupProps} title={errorJobsLabel}>
          <RunButton
            className={styles.button}
            headId={headId!}
            beforeRun={handlerSaveButtonClick}
            afterRun={() =>
              hasChanges &&
              dispatch(
                feedbackForm.actions.setVisibility({
                  visible: true,
                  reason: ShowFeedbackFormReasons.SAVE_AND_RUN,
                }),
              )
            }
            disabled={hasErrors}
            primary
          >
            {hasChanges ? 'Save and run' : 'Run'}
          </RunButton>
        </Tooltip>
      </div>
    </div>
  )
}

export default memo<Props>(SaveChanges)
